import React, { useState, useEffect } from 'react';
import { db, isConfigured } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';

export default function AddReview() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    rating: '5',
    review: '',
    projectTitle: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const MAX_REVIEW_LENGTH = 300; // Maximum characters for review

  useEffect(() => {
    // Get parameters from URL
    const params = new URLSearchParams(window.location.search);
    const project = params.get('project');
    const client = params.get('client');

    if (project) {
      setFormData(prev => ({
        ...prev,
        projectTitle: decodeURIComponent(project),
        name: client ? decodeURIComponent(client) : ''
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isConfigured) {
      setError('System not configured. Please contact administrator.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await addDoc(collection(db, 'reviews'), {
        ...formData,
        ratings: `${formData.rating} star`,
        createdAt: new Date().toISOString(),
        approved: false // Admin can approve before showing publicly
      });

      setSubmitted(true);
      setFormData({
        name: '',
        company: '',
        rating: '5',
        review: '',
        projectTitle: ''
      });
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className='min-h-screen bg-[#FFB7CE] flex items-center justify-center px-4'>
        <div className='bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center'>
          <div className='mb-6'>
            <div className='w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg className='w-10 h-10 text-green-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
              </svg>
            </div>
            <h2 className='text-3xl font-bold text-[#FFB7CE] mb-4'>Thank You! 🎉</h2>
            <p className='text-gray-600 mb-6'>
              Your review has been submitted successfully. We truly appreciate your feedback!
            </p>
            <a
              href='/'
              className='inline-block bg-[#FFB7CE] hover:bg-[#FF9BB8] text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200'
            >
              Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[#FFB7CE] py-12 px-4'>
      <div className='max-w-2xl mx-auto'>
        {/* Header */}
        <div className='text-center mb-8'>
          <h1 className='text-4xl font-bold text-white mb-2'>KHULWAEL</h1>
          <p className='text-white text-lg'>Share Your Experience</p>
        </div>

        {/* Form Card */}
        <div className='bg-white rounded-2xl shadow-2xl p-8'>
          <h2 className='text-2xl font-bold text-gray-800 mb-2'>Leave a Review</h2>
          <p className='text-gray-600 mb-6'>
            Your feedback helps us improve and showcases our work to future clients.
          </p>

          {error && (
            <div className='bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded'>
              <p>{error}</p>
            </div>
          )}

          <div className='space-y-6'>
            {/* Name */}
            <div>
              <label className='block text-gray-700 font-medium mb-2'>
                Your Name <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                name='name'
                value={formData.name}
                onChange={handleChange}
                required
                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE] transition-all'
                placeholder='John Doe'
              />
            </div>

            {/* Company */}
            <div>
              <label className='block text-gray-700 font-medium mb-2'>
                Company/Organization <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                name='company'
                value={formData.company}
                onChange={handleChange}
                required
                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE] transition-all'
                placeholder='ABC Company'
              />
            </div>

            {/* Project Title */}
            <div>
              <label className='block text-gray-700 font-medium mb-2'>
                Project Name <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                name='projectTitle'
                value={formData.projectTitle}
                onChange={handleChange}
                required
                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE] transition-all'
                placeholder='E-commerce Website'
              />
            </div>

            {/* Rating */}
            <div>
              <label className='block text-gray-700 font-medium mb-2'>
                Rating <span className='text-red-500'>*</span>
              </label>
              <div className='flex gap-2'>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type='button'
                    onClick={() => setFormData(prev => ({ ...prev, rating: star.toString() }))}
                    className='focus:outline-none transition-transform hover:scale-110'
                  >
                    <svg
                      className={`w-10 h-10 ${
                        parseInt(formData.rating) >= star
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z'
                      />
                    </svg>
                  </button>
                ))}
              </div>
              <p className='text-sm text-gray-500 mt-2'>
                {formData.rating} star{formData.rating !== '1' ? 's' : ''}
              </p>
            </div>

            {/* Review */}
            <div>
              <label className='block text-gray-700 font-medium mb-2'>
                Your Review <span className='text-red-500'>*</span>
              </label>
              <textarea
                name='review'
                value={formData.review}
                onChange={handleChange}
                required
                rows='6'
                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFB7CE] transition-all resize-none'
                placeholder='Share your experience working with us...'
              />
              <p className='text-sm text-gray-500 mt-2'>
                {formData.review.length} characters
              </p>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting || !formData.name || !formData.company || !formData.review || !formData.projectTitle}
              className='w-full bg-[#FFB7CE] hover:bg-[#FF9BB8] text-white font-bold py-4 rounded-lg transition-colors duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed'
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className='text-center mt-8 text-white text-sm'>
          <p>© {new Date().getFullYear()} KHULWAEL. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}